import { utilities } from 'utils/utilities';

export interface EnrollmentDto {
  id: string;
  enrollmentType: string;
  dtoType: string; // 'Models.Api.Dtos.EnrollmentDto';
}

// Enrollment.ts — make this an abstract base so concrete enrollments (like FormEnrollment)
// must implement their own shape and toDto().
export abstract class Enrollment {
  id: string;
  abstract readonly type: string;

  constructor(id: string) {
    this.id = id;
  }

  abstract toDto(): EnrollmentDto;

  /**
   * Create an Enrollment instance from either an Enrollment instance or a plain DTO.
   * Dispatches to concrete subclass factories when possible.
   */
  private static registry: Map<string, (dto?: any) => Enrollment> = new Map();

  static register(type: string, factory: (dto?: any) => Enrollment) {
    Enrollment.registry.set(type, factory);
  }

  static fromDto(dto?: Partial<EnrollmentDto> | Enrollment | any): Enrollment {
    console.log("Enrollment DTO " + JSON.stringify(dto));
    if (!dto) {
      return new GenericEnrollment(utilities.DEFAULT_GUID, '');
    }

    if (dto instanceof Enrollment) {
      console.log("Returning existing Enrollment instance");
      return dto;
    }

    const type = dto.type ?? '';
    const factory = Enrollment.registry.get(type);
    if (factory) {
      return factory(dto);
    }

    console.log("No factory found for type " + type);

    // If no factory is registered yet, try a lazy require for known built-in types.
    // This avoids adding a top-level import (which can cause circular init problems)
    try {
      switch (type) {
        case 'Models.Enrollments.FormEnrollment': {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const mod = require('./FormEnrollment') as typeof import('./FormEnrollment');
          if (mod && typeof mod.FormEnrollment?.fromDto === 'function') {
            return mod.FormEnrollment.fromDto(dto);
          }
          break;
        }
      }
    } catch (err) {
      // ignore and fall back to GenericEnrollment
    }

    // unknown type -> return a generic concrete implementation so callers can still call toDto()
    return new GenericEnrollment(dto.id ?? utilities.DEFAULT_GUID, type);
  }
}

class GenericEnrollment extends Enrollment {
  readonly type: string;

  constructor(id: string, type: string) {
    super(id);
    this.type = type;
  }

  toDto(): EnrollmentDto {
    return { id: this.id, enrollmentType: this.type, dtoType: 'Models.Api.Dtos.EnrollmentDto' };
  }
}
  